<template>
  <div>
    <slot name="refurbished-price">
      <RefurbishedPrice
        v-if="product.type !== 'Brand new'"
        :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
        :price-when-new="product.priceWhenNew"
      />
    </slot>
    <div class="flex flex-wrap items-baseline gap-x-4">
      <slot name="first-row-price">
        <span
          class="heading-2"
          data-qa="productpage-product-price"
          data-test="productpage-product-price"
        >
          {{ i18n.price(price) }}
        </span>
        <PriceLabel
          v-if="selectedOffer.price"
          :buyback-offer
          :is-from-google-shopping
          :mobile-plan-offer="mobilePlan"
          :price="selectedOffer.price"
          :price-due-today
        />
      </slot>
    </div>

    <div class="flex flex-wrap items-center gap-x-8">
      <slot name="second-row-price">
        <OriginalPrice
          v-if="product.type !== 'Brand new'"
          :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
          :price-when-new="product.priceWhenNew"
        />
        <RevTag
          v-if="product.type === 'Brand new'"
          :label="i18n(translations.newTaglabel)"
          variant="info"
        />

        <YouSave
          v-if="selectedOffer.price"
          :buyback-offer
          :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
          :mobile-plan
          :price="selectedOffer.price"
          :price-when-new="product.priceWhenNew"
        />
      </slot>
    </div>

    <div class="flex flex-wrap items-center gap-x-8">
      <slot name="third-row-price" />
    </div>

    <div class="flex flex-col flex-wrap">
      <slot name="fourth-row-price">
        <FormattedMessage
          v-if="withAssuranceFee"
          class="caption mt-4"
          :definition="translations.qualityAssuranceFee"
        >
          <template #qualityAssuranceFeeLink>
            <ServiceFeeModal />
          </template>
        </FormattedMessage>
        <VatIncluded class="mt-4" />
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Price } from '@backmarket/http-api'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import ServiceFeeModal from '@backmarket/nuxt-layer-product-modals/ServiceFeeModal.vue'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTag } from '@ds/components/Tag'
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep'

import translations from './Price.translations'
import OriginalPrice from './components/OriginalPrice/OriginalPrice.vue'
import PriceLabel from './components/PriceLabel/PriceLabel.vue'
import RefurbishedPrice from './components/RefurbishedPrice/RefurbishedPrice.vue'
import VatIncluded from './components/VatIncluded/VatIncluded.vue'
import YouSave from './components/YouSave/YouSave.vue'

withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    price: Price
    priceDueToday?: Price | null
    product: GetProductResponse
    mobilePlan?: ReadonlyObjectDeep<MobilePlanOffer>
    withAssuranceFee?: boolean
    isFromGoogleShopping?: boolean
  }>(),
  {
    mobilePlan: undefined,
    priceDueToday: undefined,
    withAssuranceFee: false,
    isFromGoogleShopping: false,
  },
)

defineSlots<{
  'refurbished-price': () => unknown
  'first-row-price': () => unknown
  'second-row-price': () => unknown
  'third-row-price': () => unknown
  'fourth-row-price': () => unknown
}>()

const i18n = useI18n()
const { buybackOffer } = useBuybackOffer()
</script>
